var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useState, useEffect, useRef } from 'react';
import Video from 'twilio-video';
import { GaussianBlurBackgroundProcessor, VirtualBackgroundProcessor, ImageFit, isSupported } from '@twilio/video-processors';
import { DEFAULT_VIDEO_CONSTRAINTS, SELECTED_AUDIO_INPUT_KEY, SELECTED_VIDEO_INPUT_KEY } from '../../constants';
import { isPermissionDenied } from '../../helpers';
import { useDevices } from './useDevices';
export const useLocalTracks = ({ id, enableBackgrounds, onBackgroundLoading, selectedBackground }) => {
    const [audioTrack, setAudioTrack] = useState();
    const [videoTrack, setVideoTrack] = useState();
    const [isAcquiringLocalTracks, setIsAcquiringLocalTracks] = useState(false);
    const videoProcessor = useRef(null);
    useEffect(() => {
        if (!enableBackgrounds)
            return;
        if (!isSupported)
            return;
        if (!videoTrack) {
            if (videoProcessor.current)
                videoProcessor.current = null;
            return;
        }
        if (selectedBackground === 'default') {
            if (videoProcessor.current) {
                videoTrack.removeProcessor(videoProcessor.current);
                videoProcessor.current = null;
            }
            return;
        }
        onBackgroundLoading(true);
        if (selectedBackground === 'blur') {
            const blurBackground = new GaussianBlurBackgroundProcessor({
                assetsPath: '/twilio',
                maskBlurRadius: 10,
                blurFilterRadius: 10
            });
            void blurBackground.loadModel().then(() => {
                onBackgroundLoading(false);
                if (videoProcessor.current) {
                    videoTrack.removeProcessor(videoProcessor.current);
                }
                videoTrack.addProcessor(blurBackground);
                videoProcessor.current = blurBackground;
            });
            return;
        }
        const image = new Image();
        image.crossOrigin = 'Anonymous';
        image.onload = () => {
            const virtualBackground = new VirtualBackgroundProcessor({
                assetsPath: '/twilio',
                backgroundImage: image,
                fitType: ImageFit.Fill,
                maskBlurRadius: 10
            });
            void virtualBackground.loadModel().then(() => {
                onBackgroundLoading(false);
                if (videoProcessor.current) {
                    videoTrack.removeProcessor(videoProcessor.current);
                }
                videoTrack.addProcessor(virtualBackground);
                videoProcessor.current = virtualBackground;
            });
        };
        image.src = selectedBackground;
    }, [videoTrack, selectedBackground, onBackgroundLoading, enableBackgrounds]);
    const { audioInputDevices, videoInputDevices, hasAudioInputDevices, hasVideoInputDevices } = useDevices();
    const getLocalAudioTrack = useCallback((deviceId) => {
        const options = {
            name: getTrackName('mic', id)
        };
        if (deviceId)
            options.deviceId = { exact: deviceId };
        return Video.createLocalAudioTrack(options).then((newTrack) => {
            setAudioTrack(newTrack);
            return newTrack;
        });
    }, [id]);
    const getLocalVideoTrack = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        const selectedVideoDeviceId = window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY);
        const hasSelectedVideoDevice = videoInputDevices.some((device) => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId);
        const options = Object.assign(Object.assign(Object.assign({}, DEFAULT_VIDEO_CONSTRAINTS), { name: getTrackName('camera', id) }), (hasSelectedVideoDevice && {
            deviceId: { exact: selectedVideoDeviceId }
        }));
        const newTrack = yield Video.createLocalVideoTrack(options);
        setVideoTrack(newTrack);
        return newTrack;
    }), [id, videoInputDevices]);
    const removeLocalAudioTrack = useCallback(() => {
        if (audioTrack) {
            audioTrack.stop();
            setAudioTrack(undefined);
        }
    }, [audioTrack]);
    const removeLocalVideoTrack = useCallback(() => {
        if (videoTrack) {
            videoTrack.stop();
            setVideoTrack(undefined);
        }
    }, [videoTrack]);
    const acquireLocalTracks = useCallback((options) => __awaiter(void 0, void 0, void 0, function* () {
        if (!options.video && !options.audio)
            return Promise.resolve([]);
        if (!(options.audio && hasAudioInputDevices) &&
            !(options.video && hasVideoInputDevices))
            return Promise.resolve([]);
        if (isAcquiringLocalTracks ||
            (options.audio && audioTrack) ||
            (options.video && videoTrack))
            return Promise.resolve([]);
        setIsAcquiringLocalTracks(true);
        const selectedAudioDeviceId = options.audio
            ? window.localStorage.getItem(SELECTED_AUDIO_INPUT_KEY)
            : null;
        const selectedVideoDeviceId = options.video
            ? window.localStorage.getItem(SELECTED_VIDEO_INPUT_KEY)
            : null;
        const hasSelectedAudioDevice = options.audio
            ? audioInputDevices.some((device) => selectedAudioDeviceId && device.deviceId === selectedAudioDeviceId)
            : null;
        const hasSelectedVideoDevice = options.video
            ? videoInputDevices.some((device) => selectedVideoDeviceId && device.deviceId === selectedVideoDeviceId)
            : null;
        // In Chrome, it is possible to deny permissions to only audio or only video.
        // If that has happened, then we don't want to attempt to acquire the device.
        const isMicrophonePermissionDenied = options.audio
            ? yield isPermissionDenied('microphone')
            : false;
        const isCameraPermissionDenied = options.video
            ? yield isPermissionDenied('camera')
            : false;
        const shouldAcquireVideo = options.video && hasVideoInputDevices && !isCameraPermissionDenied;
        const shouldAcquireAudio = options.audio && hasAudioInputDevices && !isMicrophonePermissionDenied;
        const localTrackConstraints = {
            video: shouldAcquireVideo && Object.assign(Object.assign(Object.assign({}, DEFAULT_VIDEO_CONSTRAINTS), { name: getTrackName('camera', id) }), (hasSelectedVideoDevice && {
                deviceId: { exact: selectedVideoDeviceId }
            })),
            audio: shouldAcquireAudio && Object.assign({ name: getTrackName('mic', id) }, (hasSelectedAudioDevice && {
                deviceId: { exact: selectedAudioDeviceId }
            }))
        };
        return Video.createLocalTracks(localTrackConstraints)
            .then((tracks) => {
            const newVideoTrack = tracks.find((track) => track.kind === 'video');
            const newAudioTrack = tracks.find((track) => track.kind === 'audio');
            if (newVideoTrack) {
                setVideoTrack(newVideoTrack);
            }
            if (newAudioTrack) {
                setAudioTrack(newAudioTrack);
            }
            if (isCameraPermissionDenied && isMicrophonePermissionDenied) {
                return Promise.reject('MediaPErmissionsDenied');
            }
            if (isCameraPermissionDenied) {
                return Promise.reject('CameraPermissionsDenied');
            }
            if (isMicrophonePermissionDenied) {
                return Promise.reject('MicrophonePermissionsDenied');
            }
            return [newAudioTrack, newVideoTrack].filter((track) => track !== undefined);
        })
            .finally(() => {
            setIsAcquiringLocalTracks(false);
        });
    }), [
        audioTrack,
        videoTrack,
        isAcquiringLocalTracks,
        id,
        audioInputDevices,
        hasAudioInputDevices,
        videoInputDevices,
        hasVideoInputDevices
    ]);
    const localTracks = [audioTrack, videoTrack].filter((track) => track !== undefined);
    return {
        localTracks,
        getLocalAudioTrack,
        getLocalVideoTrack,
        getAudioAndVideoTracks: () => acquireLocalTracks({
            video: true,
            audio: true
        }),
        acquireLocalTracks,
        isAcquiringLocalTracks,
        removeLocalAudioTrack,
        removeLocalVideoTrack
    };
};
const getTrackName = (trackKind, id) => `${trackKind}-${id}-${Date.now()}`;
//# sourceMappingURL=useLocalTracks.js.map